/**
 * Capture a pixel-perfect screenshot of the current tab via the native Screen
 * Capture API. Unlike html2canvas (which rasterizes a DOM clone and fails on
 * complex CSS layouts — nested scroll containers, sticky/fixed positioning),
 * getDisplayMedia captures exactly what the browser paints, so it always
 * matches what the user sees. It shows a one-time "share this tab" prompt;
 * Chrome's `preferCurrentTab` pre-selects the current tab to minimise friction.
 *
 * Returns a JPEG data URL, or null if unsupported, denied, or cancelled.
 */
export async function captureCurrentTab(): Promise<string | null> {
  const md = navigator.mediaDevices as MediaDevices | undefined;
  if (!md?.getDisplayMedia) return null;

  let stream: MediaStream | null = null;
  try {
    stream = await md.getDisplayMedia({
      video: { frameRate: 1 },
      audio: false,
      // Chrome-only hints: pre-select the current tab and keep it in the list.
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
      surfaceSwitching: 'exclude',
    } as MediaStreamConstraints);

    const track = stream.getVideoTracks()[0];
    if (!track) return null;

    const video = document.createElement('video');
    video.srcObject = new MediaStream([track]);
    video.muted = true;
    await video.play();
    // Wait a couple of frames so the first painted frame is stable.
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    );

    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return null;

    // Downscale wide captures to keep the upload comfortably under the limit.
    const maxW = 1600;
    const s = Math.min(1, maxW / vw);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(vw * s);
    canvas.height = Math.round(vh * s);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    video.pause();
    video.srcObject = null;
    return canvas.toDataURL('image/jpeg', 0.75);
  } catch {
    return null; // denied / cancelled / unsupported
  } finally {
    stream?.getTracks().forEach((t) => t.stop());
  }
}
